/**
 * GoalSettingModal.tsx - Goal Configuration Modal Component
 *
 * This component provides a modal interface for setting and updating
 * fundraising goals with validation and formatting support.
 *
 * Key Features:
 * - Goal amount input with currency formatting
 * - Input validation and error handling
 * - Real-time formatting as user types
 * - Minimum and maximum goal validation
 * - Save and cancel functionality
 * - Responsive modal design
 *
 * Validation Rules:
 * - Minimum goal amount (e.g., $100)
 * - Maximum goal amount limits
 * - Numeric input validation
 * - Required field validation
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - currentGoal?: number - Current goal amount
 * - onSave: (goal: number) => void - Save goal handler
 *
 * Dependencies: Styled components, portal rendering, currency formatting
 */

import React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { X } from "lucide-react";
import { PrimaryButton, SecondaryButton } from "./StyledComponents";

const GoalModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000000;
  backdrop-filter: blur(4px);
  padding: 20px;
`;

const GoalModalContent = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  width: 90%;
  max-width: 400px;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const GoalModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px 0;
  text-align: center;
`;

const GoalInputContainer = styled.div`
  position: relative;
`;

const GoalCurrencySymbol = styled.span`
  position: absolute;
  left: 16px;
  top: 14px;
  font-size: 16px;
  font-weight: 600;
  color: #64748b;
  z-index: 1;
  display: flex;
  align-items: center;
`;

const GoalInput = styled.input`
  width: 100%;
  padding: 14px 16px 14px 40px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  margin-bottom: 24px;
  height: 48px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  /* Hide number input spinners */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const ClearGoalButton = styled.button<{ visible: boolean }>`
  position: absolute;
  right: 12px;
  top: 14px;
  width: 20px;
  height: 20px;
  border: none;
  background: #f1f5f9;
  border-radius: 50%;
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;

  &:hover {
    background: #e2e8f0;
    color: #374151;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.9);
  }
`;

const ValidationText = styled.div`
  position: absolute;
  right: 56px;
  top: 14px;
  font-size: 12px;
  font-weight: 600;
  color: #dc2626;
  pointer-events: none;
  white-space: nowrap;
  display: flex;
  align-items: center;
  height: 20px;
`;

const GoalModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

interface GoalSettingModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSave: (amount: number) => void;
  initialGoal?: number;
  fundraiserGoal?: number;
  title?: string;
}

export const GoalSettingModal: React.FC<GoalSettingModalProps> = ({
  isVisible,
  onClose,
  onSave,
  initialGoal = 0,
  fundraiserGoal,
  title = "Set Promoter Goal",
}) => {
  const [goalAmount, setGoalAmount] = React.useState("");
  const [goalValidationError, setGoalValidationError] = React.useState("");

  // Initialize goal amount when modal opens
  React.useEffect(() => {
    if (isVisible) { 
      setGoalAmount(initialGoal > 0 ? initialGoal.toString() : "");
      setGoalValidationError("");
    }
  }, [isVisible, initialGoal]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleGoalAmountChange = (value: string) => {
    // Allow only numbers and decimal points
    const numericValue = value.replace(/[^0-9.]/g, "");
    setGoalAmount(numericValue);

    // Validate against fundraiser goal if provided
    const amount = parseFloat(numericValue) || 0;

    if (fundraiserGoal && fundraiserGoal > 0 && amount > fundraiserGoal) {
      setGoalValidationError(`Max ${formatCurrency(fundraiserGoal)}`);
    } else {
      setGoalValidationError("");
    }
  };

  const handleSave = () => {
    // Convert to integer, handling decimals by truncating
    const numericValue = parseFloat(goalAmount) || 0;
    const amount = Math.floor(Math.max(0, numericValue)); // Ensure non-negative integer
    onSave(amount);
    onClose();
  };

  const clearGoal = () => {
    setGoalAmount("");
    setGoalValidationError("");
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isVisible) return null;

  return createPortal(
    <GoalModalOverlay visible={isVisible} onClick={handleOverlayClick}>
      <GoalModalContent>
        <GoalModalTitle>{title}</GoalModalTitle>
        <GoalInputContainer>
          <GoalCurrencySymbol>$</GoalCurrencySymbol>
          <GoalInput
            type="number"
            min="0"
            step="1"
            value={goalAmount}
            onChange={(e) => handleGoalAmountChange(e.target.value)}
            placeholder="0"
            autoFocus
          />
          {goalValidationError && (
            <ValidationText>{goalValidationError}</ValidationText>
          )}
          <ClearGoalButton
            visible={!!goalAmount && goalAmount !== "0"}
            onClick={clearGoal}
            type="button"
            title="Clear goal amount"
          >
            <X size={12} />
          </ClearGoalButton>
        </GoalInputContainer>
        <GoalModalActions>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSave} disabled={!!goalValidationError}>
            Save
          </PrimaryButton>
        </GoalModalActions>
      </GoalModalContent>
    </GoalModalOverlay>,
    document.body
  );
};
